import { useFormContext } from 'react-hook-form';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import ImageInput from '../../../../../shared/ImageInput';
import { InputComponent } from '../../../../../shared/InputComponent';
import { TextareaComponent } from '../../../../../shared/TextareaComponent';
import { ServicesTable } from '../../../OrderService/components/ServicesTable';
import styles from '../edit-budget-form.module.css';
import { BudgetFormDTO } from '../BudgetFormDTO';
import { parseNumber } from '../../../OrderService/CreateOrderServiceForm/parseOrderServiceForm';
import { currencyMask } from '../../../../../services/helpers/mask';
import { parseBudgetForm } from '../parseBudgetForm';
import {
  transformBudgetDraftController,
  updateBudgetDraftController,
} from '../controller';
import { LoadingSpinner } from '../../../../../shared/LoadingSpinner';
import { SendBudgetModal } from '../../components/SendBudgetModal';
import { handleWhatsappMessage } from '../../CreateBudgetForm/handleWhatsappMessage';
import { Budget } from '../../../../../@types/budget';
import { useAuth } from '../../../../../hooks/useAuth';

export const ServicesForm = () => {
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext<BudgetFormDTO>();

  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [createdBudget, setCreatedBudget] = useState<Budget | null>(null);

  const [serviceTotal, setServiceTotal] = useState(0);
  const [productTotal, setProductTotal] = useState(0);

  const [statusModal, setStatusModal] = useState<
    'sending' | 'success' | 'error'
  >('sending');

  // const productTable = watch('services.productTable') || [];
  // const serviceTable = watch('services.serviceTable') || [];
  const discount = watch('services.discount') || '0';
  const serviceImages = watch('services.images_urls') || [];

  // const serviceTotal = serviceTable.reduce((acc, currentService) => {
  //   return (
  //     acc +
  //     parseNumber(currentService.unitValue, true) *
  //       parseNumber(currentService.quantity)
  //   );
  // }, 0);

  // const productTotal = productTable.reduce((acc, currentProduct) => {
  //   return (
  //     acc +
  //     parseNumber(currentProduct.unitValue, true) *
  //       parseNumber(currentProduct.quantity)
  //   );
  // }, 0);

  const total = useMemo(
    () => serviceTotal + productTotal - parseNumber(discount, true),
    [discount, productTotal, serviceTotal],
  );

  const totalStyle = {
    color: total >= 0 ? '#000' : 'var(--error)',
  };
  const isFormValid = total > 0;

  const handleImageChange = useCallback(
    (images: string[]) => {
      setValue('services.images_urls', images);
    },
    [setValue],
  );

  const getServiceImages = () => serviceImages;
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const onSubmit = async (formData: BudgetFormDTO) => {
    try {
      const res = await updateBudgetDraftController({
        ...parseBudgetForm(formData, total),
        id_budget: formData.id_budget,
      });
      if (res) {
        setCreatedBudget(res);
        setIsBudgetModalOpen(true);
        setStatusModal('success');

        // força atualização da lista de ORÇAMENTOS
        queryClient.invalidateQueries(['budgets']);
        queryClient.invalidateQueries({ queryKey: ['filteredBudgets'] });
      }
    } catch (error) {
      console.error(error);
      setStatusModal('error');
    }
  };

  const { workshop } = useAuth();

  return (
    <form className={`${styles.form} `}>
      <SendBudgetModal
        isOpen={isBudgetModalOpen}
        handleClose={() => {
          setIsBudgetModalOpen(false);
          setStatusModal('sending');
          navigate('/budget');
        }}
        onPressMessage={() => {
          if (createdBudget) {
            handleWhatsappMessage(createdBudget, workshop?.fantasy_name ?? '');
          }
          setIsBudgetModalOpen(false);
          navigate('/budget');
        }}
        status={statusModal}
        isEditing
      />
      <div className={styles['form-row']}>
        <TextareaComponent
          {...register('services.comment')}
          label="Observações: "
          hint="Informações adicionais sobre a seu Orçamento"
          className={styles.textarea}
        />
      </div>
      <div className={styles['form-row']}>
        <InputComponent
          {...register('services.validity')}
          label="Validade:"
          placeholder="7 dias"
          className={styles.input}
        />
        <div className={styles['image-input-wrapper']}>
          <span>Enviar Imagem:</span>
          <ImageInput
            size={68}
            className={styles['image-input']}
            imageClassName={styles.image}
            imagesUrls={getServiceImages()}
            setImageUrls={handleImageChange}
            bucketPath="temp"
          />
        </div>
      </div>
      <div className={`${styles['form-column']} ${styles['border-bottom']}`}>
        <div className={styles['services-table-wrapper']}>
          <ServicesTable
            {...register('services.productTable')}
            setTotal={setProductTotal}
            type="product"
          />
        </div>
        <div className={styles['services-table-wrapper']}>
          <ServicesTable
            {...register('services.serviceTable')}
            setTotal={setServiceTotal}
            type="service"
          />
        </div>
      </div>
      <div className={`${styles['form-row']} ${styles.info}`}>
        <span>Subtotal de produtos</span>
        <span>
          {productTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>
      <div className={`${styles['form-row']} ${styles.info}`}>
        <span>Subtotal de serviços</span>
        <span>
          {serviceTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>
      <div className={`${styles['form-row']} ${styles.info}`}>
        <span>Desconto</span>
        <div className={styles['discount-input']}>
          <input
            {...register('services.discount')}
            onChange={e => {
              const newValue = currencyMask(e.target.value).replace('R$', '');
              setValue('services.discount', newValue);
            }}
            type="text"
            placeholder="R$ 00,00"
          />
        </div>
      </div>
      <div className={`${styles['form-row']} ${styles.info} ${styles.total}`}>
        <span>TOTAL</span>
        <span style={totalStyle}>
          {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>
      <div className={styles['save-form-container']}>
        <div className={styles['save-form--row']}>
          <button
            type="button"
            className={styles['second-btn']}
            disabled={!isFormValid}
            onClick={handleSubmit(onSubmit)}
          >
            Enviar
            {isSubmitting && <LoadingSpinner />}
          </button>
        </div>
      </div>
    </form>
  );
};
